import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { Avatar } from '@/components/ui/Avatar';
import { getVisibleNavSections } from '@/components/layout/Sidebar';
import { LogOut } from 'lucide-react';

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function MobileDrawer({ open, onClose }: MobileDrawerProps) {
  const { tenant, logout } = useAuth();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Gate Admin (DEM-1870): mesmo filtro do Sidebar desktop.
  const isAdmin = tenant?.is_admin ?? false;
  const visibleSections = getVisibleNavSections(isAdmin);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="md:hidden">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 z-[300]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={cn(
          'fixed left-0 top-0 bottom-0 w-72 bg-raios-surface z-[301] shadow-raios-xl',
          'flex flex-col',
          'animate-[slide-in-left_200ms_ease-out]',
        )}
      >
        {/* Logo */}
        <div className="px-5 py-5 border-b border-raios-border">
          <div className="flex items-center gap-2.5">
            <img src="/raios-bolt.png" alt="" aria-hidden="true" className="w-8 h-8" />
            <div>
              <h1 className="text-base font-bold text-raios-text tracking-tight leading-none">
                RAIOS
              </h1>
              <p className="text-2xs text-raios-muted mt-0.5 leading-none">Intelligence OS</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3 overflow-y-auto space-y-4">
          {visibleSections.map((section) => (
            <div key={section.title}>
              <p className="px-2 mb-1.5 text-2xs font-semibold uppercase tracking-widest text-raios-muted">
                {section.title}
              </p>
              <div className="space-y-0.5">
                {section.items.map((item) => item.disabled ? (
                  <div
                    key={item.to}
                    aria-disabled="true"
                    className="flex select-none items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-raios-text-secondary opacity-50"
                  >
                    <item.icon className="h-[18px] w-[18px] shrink-0 text-raios-muted" strokeWidth={1.8} />
                    <span className="flex-1">{item.label}</span>
                    {item.badge === 'soon' && (
                      <span className="rounded-sm bg-raios-elevated/50 px-1 py-px text-[8.5px] font-medium uppercase tracking-wider text-raios-muted">
                        em breve
                      </span>
                    )}
                  </div>
                ) : (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={onClose}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-all duration-150',
                          isActive
                            ? 'bg-raios-accent/15 text-raios-accent-text font-medium'
                            : 'text-raios-text-secondary hover:bg-raios-elevated hover:text-raios-text',
                        )
                      }
                    >
                      <item.icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.8} />
                      <span>{item.label}</span>
                      {item.badge === 'legacy' && (
                        <span className="ml-auto rounded-sm bg-raios-elevated/50 px-1 py-px text-[8.5px] font-medium uppercase tracking-wider text-raios-muted">
                          legado
                        </span>
                      )}
                    </NavLink>
                  ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User section */}
        <div className="px-4 py-4 border-t border-raios-border">
          <div className="flex items-center gap-3">
            <Avatar size="sm" name={tenant?.name} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-raios-text truncate">
                {tenant?.name ?? 'Carregando...'}
              </p>
              <p className="text-2xs text-raios-muted truncate">{tenant?.slug}</p>
            </div>
            <button
              onClick={logout}
              className="text-raios-muted hover:text-raios-danger transition-colors shrink-0"
              title="Sair"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
